import { Dayjs } from "dayjs";
import dayjs, { dayJsLocale } from "utils/dayjs";

export enum DateFormatStyle {
  NumericDate = "L",
  TimeOnly = "LT",
  MonthDayDate = "ll",
  NumericDateTime = "L LT",
  MonthDayDateTime = "ll • LT",
  DayMonthYear = "DD MMM YYYY",
  DayMonthYearTime = "DD MMM YYYY • LT",
  DayMonthYearDash = "DD-MM-YYYY",
  MonthDayYearDash = "MM-DD-YYYY",
  DayMonthYearSlash = "DD/MM/YYYY",
  MonthDayYearSlash = "MM/DD/YYYY"
}

/**
 * Returns the current browser timezone
 */
export function currentIANATimeZone() {
  return dayjs.tz.guess();
}

/**
 * Returns the current browser display timezone.
 */
export function currentDisplayTimeZone() {
  return (
    Intl.DateTimeFormat(undefined, {
      timeZoneName: "long"
    })
      .formatToParts(new Date())
      .find(f => f.type === "timeZoneName")?.value ?? "UTC"
  );
}

/**
 * Format the datetime into some prespecified formats
 */
export function formatDateTime(
  date: string | Date | Dayjs,
  format: DateFormatStyle,
  timeZone: string | undefined = undefined,
  includeTimeZone = false
): string {
  let timeZoneFormatString = "";
  if (includeTimeZone) {
    timeZoneFormatString = " z";
  }

  return dayjs(date)
    .tz(timeZone)
    .locale(dayJsLocale)
    .format(`${format}${timeZoneFormatString}`);
}

/**
 * This is used to offset a date to the specified timezone, this is used where we display a time in a dynamic timezone, eg. with a dropdown
 */
export function datetimeInSpecifiedTimeZone(
  date: string | Date | Dayjs,
  specifiedTimeZone?: string
): Dayjs {
  const offset =
    -dayjs(date).utcOffset() + dayjs(date).tz(specifiedTimeZone).utcOffset();
  return dayjs(date).add(offset, "minute");
}

/**
 * This is used to offset a date to the current timezone, this is used where we display a time in a dynamic timezone, eg. with a dropdown
 */
export function dateTimeInCurrentTimeZone(date: string | Date | Dayjs): Dayjs {
  const offset =
    -dayjs(date).utcOffset() +
    dayjs(date).tz(currentIANATimeZone()).utcOffset();
  return dayjs(date).add(offset, "minute");
}

/**
 * This is used to restore a date from a specified timezone, this is used to restore the time to the original
 */
export function datetimeFromSpecifiedTimeZone(
  date: string | Date | Dayjs,
  specifiedTimeZone?: string
): Dayjs {
  const offset =
    -dayjs(date).utcOffset() + dayjs(date).tz(specifiedTimeZone).utcOffset();
  return dayjs(date).subtract(offset, "minute");
}

export function isWithIn120DaysAway(startTime: Date | undefined): boolean {
  if (startTime) {
    return dayjs(startTime).diff(Date.now(), "day") < 121;
  }

  return false;
}
